/**
 * V4CognitiveBriefSlideout — Phase 7h.
 *
 * Right-edge slideout that edits an agent's Cognitive Brief — the
 * LLM-runtime config the cognitive_loop node uses at execution time.
 * Opens when the operator double-clicks a cognitive_loop node on the
 * canvas (host wires the trigger).
 *
 * V3 parity:
 *   • Same 5 fields, same JSON keys, same PUT /api/v1/agents/{id}
 *     payload shape `{ cognitiveBrief: {...} }` — see
 *     CanvasPageV3.jsx:7653-7845 + line 2038-2044.
 *   • Brief lives on the AGENT record, not the canvas node. Saving
 *     updates the agent; the cognitive_loop node's render is a
 *     read-time copy refreshed on the next canvas load. No canvas
 *     dirty / re-validate / sync side effects (V3 didn't have any
 *     either — runtime executor reads the brief at exec time).
 *
 * Contract:
 *   • Props are pure — slideout owns its FORM state, host owns the
 *     async save via `onSave(brief)`. Host handles toast + agent
 *     refetch after success.
 *   • Validation gates the Save button locally (no round-trip required
 *     to learn that systemPrompt is blank).
 *   • Esc / Cancel close without saving. Backdrop click does NOT close
 *     while saving (avoids tab-out wiping operator input).
 *
 * Renders only when `open && agent`. If the host opens it without an
 * agent (e.g. workflow has cognitive_loop but no workflow.agentId),
 * the host should surface a toast and not open the slideout.
 *
 * Tagged PHASE-7H.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Brain, X } from 'lucide-react';
import {
  S, BRAND, ACCENT, SPACING, RADIUS, FONT, WEIGHT, SHADOW, Z,
  primaryBtn, secondaryBtn, ghostBtn, v4Input,
} from './v4Tokens';

/* ───── Field defaults + enum domains — V3 parity ────────────────── */
const TOOL_STRATEGIES = [
  { value: 'auto',            label: 'Auto',             desc: 'Model picks tools as needed (default).' },
  { value: 'forced_sequence', label: 'Forced sequence',  desc: 'Run tools in declared order; skip if not applicable.' },
  { value: 'react',           label: 'ReAct',            desc: 'Reason-then-act loop (think → call → observe).' },
  { value: 'tot',             label: 'Tree of thought',  desc: 'Explore branches, prune low-confidence paths.' },
];

const FALLBACK_BEHAVIORS = [
  { value: 'stop',      label: 'Stop',          desc: 'Abort the run; surface the last error to the operator.' },
  { value: 'escalate',  label: 'Escalate',      desc: 'Route to the on-call agent owner for adjudication.' },
  { value: 'ask_human', label: 'Ask a human',   desc: 'Pause the run and open a human-task approval.' },
];

const MAX_ITERATIONS_MIN = 1;
const MAX_ITERATIONS_MAX = 200;
const MAX_ITERATIONS_DEFAULT = 10;

const DEFAULT_FORM = Object.freeze({
  systemPrompt: '',
  toolSelectionStrategy: 'auto',
  maxIterations: MAX_ITERATIONS_DEFAULT,
  iterationCostCapUsd: '',   /* string in the input — coerced on save */
  fallbackBehavior: 'stop',
});

function seedFromBrief(brief) {
  if (!brief || typeof brief !== 'object') return { ...DEFAULT_FORM };
  const maxIter = Number.isFinite(brief.maxIterations) ? brief.maxIterations : MAX_ITERATIONS_DEFAULT;
  const cap = brief.iterationCostCapUsd;
  return {
    systemPrompt: typeof brief.systemPrompt === 'string' ? brief.systemPrompt : '',
    toolSelectionStrategy: TOOL_STRATEGIES.some((s) => s.value === brief.toolSelectionStrategy)
      ? brief.toolSelectionStrategy
      : 'auto',
    maxIterations: maxIter,
    iterationCostCapUsd: cap == null ? '' : String(cap),
    fallbackBehavior: FALLBACK_BEHAVIORS.some((f) => f.value === brief.fallbackBehavior)
      ? brief.fallbackBehavior
      : 'stop',
  };
}

export default function V4CognitiveBriefSlideout({
  open,
  agent,            // linked agent object; null when not loaded yet
  saving = false,   // host-owned saving flag (in-flight PUT)
  onSave,           // (brief) => Promise<void> — host calls api.updateAgent
  onCancel,         // () => void
}) {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [localError, setLocalError] = useState(null);
  const firstFieldRef = useRef(null);

  /* Re-seed on every open. Otherwise the slideout would show stale
   * form state from the previous open if the operator's last action
   * was Cancel (we'd remember their dropped edits). */
  useEffect(() => {
    if (!open) return;
    setForm(seedFromBrief(agent?.cognitiveBrief));
    setLocalError(null);
    const t = requestAnimationFrame(() => {
      if (firstFieldRef.current) firstFieldRef.current.focus();
    });
    return () => cancelAnimationFrame(t);
  }, [open, agent?.id, agent?.cognitiveBrief]);

  /* Esc closes — unless saving. */
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) {
        e.preventDefault();
        onCancel?.();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, saving, onCancel]);

  const validation = useMemo(() => {
    if (!form.systemPrompt || !form.systemPrompt.trim()) {
      return 'System prompt is required — describe what this agent should do.';
    }
    if (!Number.isFinite(form.maxIterations)
      || form.maxIterations < MAX_ITERATIONS_MIN
      || form.maxIterations > MAX_ITERATIONS_MAX) {
      return `Max iterations must be between ${MAX_ITERATIONS_MIN} and ${MAX_ITERATIONS_MAX}.`;
    }
    if (form.iterationCostCapUsd !== '' && form.iterationCostCapUsd != null) {
      const n = Number(form.iterationCostCapUsd);
      if (Number.isNaN(n) || n < 0) {
        return 'Cost cap must be a non-negative number (or blank to inherit the org default).';
      }
    }
    return null;
  }, [form]);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const handleSubmit = async () => {
    if (validation) { setLocalError(validation); return; }
    if (typeof onSave !== 'function') return;
    setLocalError(null);
    const costCapRaw = form.iterationCostCapUsd;
    const brief = {
      systemPrompt: form.systemPrompt.trim(),
      toolSelectionStrategy: form.toolSelectionStrategy,
      maxIterations: Math.trunc(form.maxIterations),
      iterationCostCapUsd: (costCapRaw === '' || costCapRaw == null)
        ? null
        : Number(costCapRaw),
      fallbackBehavior: form.fallbackBehavior,
    };
    try {
      await onSave(brief);
    } catch (e) {
      setLocalError(e?.message || String(e));
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Cognitive Brief editor"
      onMouseDown={(e) => {
        /* Backdrop click closes — but only when idle. While a save is
         * in flight we ignore the click so a stray tab-out doesn't
         * blow away the operator's edits before the PUT lands. */
        if (e.target === e.currentTarget && !saving) onCancel?.();
      }}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15, 23, 42, 0.35)',
        zIndex: Z.modal,
        display: 'flex', justifyContent: 'flex-end',
      }}
    >
      <aside style={{
        width: 'min(560px, 100vw)',
        height: '100%',
        background: '#ffffff',
        display: 'flex', flexDirection: 'column',
        boxShadow: SHADOW.popover,
        fontFamily: 'inherit',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: SPACING.s,
          padding: `${SPACING.m}px ${SPACING.l}px`,
          borderBottom: `0.5px solid ${S[200]}`,
        }}>
          <Brain size={15} aria-hidden="true" style={{ color: BRAND.icon }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: FONT.lg, fontWeight: WEIGHT.semibold, color: S[900] }}>
              Cognitive Brief
            </div>
            <div style={{
              fontSize: FONT.xs, color: S[500],
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {agent?.name ? `Editing ${agent.name}` : 'Editing this agent'}
              {' · changes apply on the next execution'}
            </div>
          </div>
          <button
            type="button"
            onClick={() => { if (!saving) onCancel?.(); }}
            aria-label="Close"
            style={ghostBtn}
            disabled={saving}
          >
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        <div style={{
          flex: 1, minHeight: 0,
          padding: SPACING.l,
          overflowY: 'auto',
          display: 'flex', flexDirection: 'column', gap: SPACING.l,
        }}>
          {!agent && (
            <Placeholder>Loading agent…</Placeholder>
          )}

          {agent && (
            <>
              {/* System prompt */}
              <Field
                label="System prompt"
                required
                hint="Describe the agent's role, goals, and operating boundaries. The model sees this on every iteration."
              >
                <textarea
                  ref={firstFieldRef}
                  value={form.systemPrompt}
                  onChange={(e) => set({ systemPrompt: e.target.value })}
                  rows={8}
                  placeholder="You are a triage assistant. Your job is to…"
                  style={{
                    ...v4Input,
                    fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                    minHeight: 160,
                    resize: 'vertical',
                  }}
                />
              </Field>

              {/* Tool strategy + Fallback — two-column row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACING.m }}>
                <Field
                  label="Tool selection strategy"
                  hint={TOOL_STRATEGIES.find((s) => s.value === form.toolSelectionStrategy)?.desc}
                >
                  <select
                    value={form.toolSelectionStrategy}
                    onChange={(e) => set({ toolSelectionStrategy: e.target.value })}
                    style={v4Input}
                  >
                    {TOOL_STRATEGIES.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </Field>

                <Field
                  label="Fallback behavior"
                  hint={FALLBACK_BEHAVIORS.find((f) => f.value === form.fallbackBehavior)?.desc}
                >
                  <select
                    value={form.fallbackBehavior}
                    onChange={(e) => set({ fallbackBehavior: e.target.value })}
                    style={v4Input}
                  >
                    {FALLBACK_BEHAVIORS.map((f) => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                </Field>
              </div>

              {/* Max iterations + Cost cap — two-column row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACING.m }}>
                <Field
                  label="Max iterations"
                  hint={`Hard ceiling on think → act → observe cycles per run (${MAX_ITERATIONS_MIN}–${MAX_ITERATIONS_MAX}).`}
                >
                  <input
                    type="number"
                    min={MAX_ITERATIONS_MIN}
                    max={MAX_ITERATIONS_MAX}
                    step={1}
                    value={form.maxIterations}
                    onChange={(e) => {
                      const v = e.target.value;
                      const n = Number(v);
                      set({ maxIterations: Number.isFinite(n) ? n : v });
                    }}
                    style={v4Input}
                  />
                </Field>

                <Field
                  label="Cost cap (USD per run)"
                  hint="Leave blank to inherit the organization default."
                >
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.iterationCostCapUsd}
                    onChange={(e) => set({ iterationCostCapUsd: e.target.value })}
                    placeholder="e.g. 2.50"
                    style={v4Input}
                  />
                </Field>
              </div>
            </>
          )}

          {(localError) && (
            <div style={{
              padding: SPACING.s + 2,
              fontSize: FONT.sm,
              color: ACCENT.rose.text,
              background: ACCENT.rose.bg,
              border: `0.5px solid ${ACCENT.rose.icon}`,
              borderRadius: RADIUS.md,
            }}>
              {localError}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: SPACING.s,
          padding: `${SPACING.s + 2}px ${SPACING.l}px`,
          borderTop: `0.5px solid ${S[200]}`,
          background: S[50],
        }}>
          <span style={{ fontSize: FONT.xs, color: S[500] }}>
            {validation
              ? validation
              : 'Saved to the agent record · re-fetches on success'}
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: SPACING.s }}>
            <button
              type="button"
              onClick={() => { if (!saving) onCancel?.(); }}
              style={secondaryBtn}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!agent || !!validation || saving}
              style={{
                ...primaryBtn,
                opacity: (!agent || !!validation || saving) ? 0.5 : 1,
                cursor: (!agent || !!validation || saving) ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? 'Saving…' : 'Save Brief'}
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}

/* ───── helpers ────────────────────────────────────────────────────── */
function Field({ label, required, hint, children }) {
  return (
    <div>
      <label style={{
        display: 'flex', alignItems: 'center', gap: SPACING.xs,
        fontSize: FONT.sm, fontWeight: WEIGHT.semibold, color: S[800],
        marginBottom: SPACING.xs,
      }}>
        {label}
        {required && <span style={{ color: BRAND.icon }}>*</span>}
      </label>
      {children}
      {hint && (
        <div style={{
          marginTop: SPACING.xs,
          fontSize: FONT.xs, color: S[500],
        }}>
          {hint}
        </div>
      )}
    </div>
  );
}

function Placeholder({ children }) {
  return (
    <div style={{
      padding: SPACING.l, textAlign: 'center',
      fontSize: FONT.sm, color: S[500],
    }}>
      {children}
    </div>
  );
}
